import { useEffect, useRef, useState, useCallback } from 'react';

function weaponIdsSignature(unit) {
  const list = unit?.equippedWeapons;
  if (!Array.isArray(list) || list.length === 0) return '';
  return list
    .map((w) => (w && w.weaponInstanceId != null ? String(w.weaponInstanceId) : ''))
    .filter(Boolean)
    .sort()
    .join(',');
}

/**
 * Detects newly attached weapons on units (equippedWeapons gains entries) and drives equip_weapon.png flashes (~1s).
 */
export function useWeaponEquipFlash(gameState, viewerUserId) {
  const [flashes, setFlashes] = useState([]);
  const weaponIdsByUnitRef = useRef(new Map());
  const seededRef = useRef(false);
  const gameIdRef = useRef(null);

  const dismissFlash = useCallback((key) => {
    setFlashes((prev) => prev.filter((f) => f.key !== key));
  }, []);

  useEffect(() => {
    if (!gameState?.state || viewerUserId == null) {
      return;
    }

    const gid = gameState.gameId ?? null;
    if (gid != null && gameIdRef.current !== gid) {
      gameIdRef.current = gid;
      seededRef.current = false;
      weaponIdsByUnitRef.current = new Map();
    }

    const allPlayerIds = gameState.players || Object.keys(gameState.state || {});

    if (!seededRef.current) {
      allPlayerIds.forEach((playerId) => {
        const player = gameState.state[playerId];
        if (!player?.playedOrders) return;
        ['front', 'flank'].forEach((zone) => {
          const row = player.playedOrders[zone];
          if (!row) return;
          row.forEach((unit) => {
            if (!unit?.instanceId) return;
            const unitKey = `${playerId}_${unit.instanceId}`;
            weaponIdsByUnitRef.current.set(unitKey, weaponIdsSignature(unit));
          });
        });
      });
      seededRef.current = true;
      return;
    }

    const newFlashes = [];

    allPlayerIds.forEach((ownerId) => {
      const player = gameState.state[ownerId];
      if (!player?.playedOrders) return;

      ['front', 'flank'].forEach((zone) => {
        const row = player.playedOrders[zone];
        if (!row) return;
        row.forEach((unit, index) => {
          if (!unit?.instanceId) return;
          const unitKey = `${ownerId}_${unit.instanceId}`;
          const prevSig = weaponIdsByUnitRef.current.get(unitKey) ?? '';
          const nextSig = weaponIdsSignature(unit);

          if (prevSig === nextSig) {
            return;
          }

          const prevSet = new Set(prevSig ? prevSig.split(',').filter(Boolean) : []);
          const weapons = unit.equippedWeapons || [];
          weapons.forEach((w) => {
            if (!w || w.weaponInstanceId == null) return;
            const wid = String(w.weaponInstanceId);
            if (prevSet.has(wid)) return;
            const prefix = String(ownerId) === String(viewerUserId) ? 'your' : 'opponent';
            const viewerZone = `${prefix}_${zone}`;
            newFlashes.push({
              key: `wep-flash-${wid}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              viewerZone,
              index,
            });
          });

          weaponIdsByUnitRef.current.set(unitKey, nextSig);
        });
      });
    });

    if (newFlashes.length > 0) {
      setFlashes((prev) => [...prev, ...newFlashes]);
    }
  }, [gameState, gameState?.stateVersion, viewerUserId]);

  return { flashes, dismissFlash };
}

export default useWeaponEquipFlash;
